import { useState, useEffect, useRef } from "react";
import { Popover } from "../../../../components/Popover";
import { TextField } from "../../../../components/TextField";
import Button from "../../../../components/Button";
import Flex from "../../../../components/Flex";
import LinkOff from "@servicetitan/hammer-icon/mdi/round/link_off.svg";

type LinkPopoverProps = {
  open: boolean;
  anchorRect: DOMRect | null;
  initialHref: string;
  initialDisplayText: string;
  onSave: (href: string, displayText: string) => void;
  onRemove: () => void;
  onCancel: () => void;
};

export const LinkPopover = ({
  open,
  anchorRect,
  initialHref,
  initialDisplayText,
  onSave,
  onRemove,
  onCancel,
}: LinkPopoverProps) => {
  const [href, setHref] = useState(initialHref);
  const [displayText, setDisplayText] = useState(initialDisplayText);
  const hrefInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setHref(initialHref);
      setDisplayText(initialDisplayText);
      hrefInputRef.current?.focus();
    }
  }, [open, initialHref, initialDisplayText]);

  const handleSave = () => {
    onSave(href, displayText);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSave();
    }
    if (e.key === "Escape") onCancel();
  };

  return (
    <Popover open={open} placement="bottom" onClose={onCancel}>
      <Popover.Trigger>
        {({ ref }) => (
          <button
            ref={ref}
            type="button"
            tabIndex={-1}
            aria-hidden
            style={{
              position: "fixed",
              top: anchorRect?.bottom ?? 0,
              left: anchorRect?.left ?? 0,
              width: anchorRect?.width ?? 0,
              height: 0,
              visibility: "hidden",
              pointerEvents: "none",
            }}
          />
        )}
      </Popover.Trigger>
      <Popover.Content>
        <Flex
          direction="column"
          gap={3}
          style={{ minWidth: 280 }}
          onKeyDown={handleKeyDown}
        >
          <TextField
            ref={hrefInputRef}
            label="Link address"
            required
            value={href}
            onChange={(e) => setHref(e.target.value)}
            placeholder="https://"
          />
          <TextField
            label="Display Text"
            value={displayText}
            onChange={(e) => setDisplayText(e.target.value)}
          />
          <Flex gap={2} justifyContent="space-between">
            {initialHref ? (
              <Button
                appearance="danger"
                size="small"
                icon={LinkOff}
                aria-label="Remove link"
                onClick={onRemove}
              />
            ) : (
              <span />
            )}
            <Flex gap={2}>
              <Button appearance="ghost" size="small" onClick={onCancel}>
                Cancel
              </Button>
              <Button
                appearance="primary"
                size="small"
                disabled={!href.trim()}
                onClick={handleSave}
              >
                Save
              </Button>
            </Flex>
          </Flex>
        </Flex>
      </Popover.Content>
    </Popover>
  );
};

LinkPopover.displayName = "LinkPopover";
